<template>
  <div class="drawer-settings extra-padding-x overflow-auto text-sm">

    <h1 class="drawer-header ">
      <template v-if="!dbReady">Loading settings...</template>
      <template v-else>
        Settings
      </template>
    </h1>

    <p v-if="dbReady" class="mb-2 select-none">Some settings are disabled while recording a new activity.</p>

    <hr class="divider divider-light">

    <!-- @click="toggleOuterGroup($event)" -->
    <h2 class="headline">
      Global App Permissions
      <svg class="w-4 h-4 text-pink-200 pointer-events-none">
        <use xlink:href="#plus-circle" class="pointer-events-none" />
        <use xlink:href="#minus-circle" class="pointer-events-none invisible" />
      </svg>
    </h2>

    <!-- <div class="outer-group-wrapper"> -->
      <!-- <div class="outer-group"> -->
        <!-- Allow tracking -->
        <div class="option-wrapper" :class="{ 'cursor-not-allowed opacity-50': recording }">
          <div class="option">
            <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
            <div class="mr-2 font-bold leading-none">Allow Tracking</div>
            <!--
              ":disabled" is a boolean, but "recording" could also be a string ("paused"),
              so check against "recording === 1"
            -->
            <toggle-button
              class="ml-auto"
              color="#059605"
              @change="onChangeTrackingAllowedOnOff"
              :sync="true"
              :value="trackingAllowed"
              :disabled="recording === 1"
              :labels="true" />
          </div>
          <div class="option-child__info--wrapper">
            <div class="option-child__info">
              Activate this setting if you want to allow your device to use Geolocation to track and record your (sportive) activities.
            </div>
          </div>
        </div>

        <div class="settings-block w-full" :class="{ 'hidden': !trackingAllowed }">

          <!-- Navigator geolocation -->

          <p class="headline flex justify-between items-center" @click="toggleOptionsBlock($event, 'navigatorsettings')">
            Navigator Geolocation
            <svg class="w-4 h-4 text-pink-200">
              <use xlink:href="#plus-circle" class="pointer-events-none" />
              <use xlink:href="#minus-circle" class="pointer-events-none invisible" />
            </svg>
          </p>

          <div class="options-block-wrapper" data-navigatorsettings>

            <div class="options-block">

              <div class="option-wrapper" :class="{ 'cursor-not-allowed opacity-50': recording }">
                <div class="option">
                  <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
                  <div class="mr-2 font-bold leading-none">Enable High Accuracy</div>
                  <toggle-button
                    class="ml-auto"
                    @change="onChangeEnableHighAccuracyOnOff"
                    color="#059605"
                    :sync="true"
                    :value="enableHighAccuracy"
                    :disabled="recording === 1"
                    :labels="true" />
                </div>
                <div class="option-child__info--wrapper">
                  <div class="option-child__info">Increases battery usage.</div>
                </div>
              </div>

              <div class="option-wrapper">
                <div class="option">
                  <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
                  <div class="mr-2 font-bold leading-none">Timeout</div>
                  <input type="text" v-model="timeout" class="w-16 h-6 ml-auto px-2 py-0 text-gray-700 text-sm rounded text-right flex-grow-0"/>
                </div>
                <div class="option-child__info--wrapper">
                  <div class="option-child__info">Maximum duration (in milliseconds) a geolocation request may last.</div>
                </div>
              </div>

              <div class="option-wrapper">
                <div class="option">
                  <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
                  <div class="mr-2 font-bold leading-none">Maximum Age</div>
                  <input type="text" v-model="maximumAge" class="w-16 h-6 ml-auto px-2 py-0 text-gray-700 text-sm rounded text-right flex-grow-0"/>
                </div>
                <div class="option-child__info--wrapper">
                  <div class="option-child__info">Amount in milli-seconds, within which no new value is calculated as long as the device does not move.</div>
                </div>
              </div>

            </div>

          </div>

          <!-- Geolocation recording constraints -->

          <p class="headline flex justify-between items-center" @click="toggleOptionsBlock($event, 'recordingconstraints')">
            Geolocation Recording Constrains
            <svg class="w-4 h-4 text-pink-200">
              <use xlink:href="#plus-circle" class="pointer-events-none" />
              <use xlink:href="#minus-circle" class="pointer-events-none invisible" />
            </svg>
          </p>

          <div class="options-block-wrapper" data-recordingconstraints>

            <div class="options-block">

              <div class="option-wrapper">
                <div class="option">
                  <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
                  <div class="mr-2 font-bold leading-none">Use minimum speed</div>
                  <toggle-button
                    class="ml-auto"
                    color="#059605"
                    @change="onChangeUseMinSpeedThresholdOnOff"
                    :sync="true"
                    :value="useMinSpeedThreshold"
                    :labels="true" />
                </div>
                <div class="option-child__info--wrapper">
                  <div class="option-child__info">Use geolocation's speed parameter to detect if you are moving at a certain amount (in meters / second).</div>
                </div>
              </div>

              <div class="option-wrapper" :class="{ 'hidden': !useMinSpeedThreshold }">
                <div class="option">
                  <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
                  <div class="mr-2 font-bold leading-none">Minimum Speed</div>
                  <input type="text" v-model="minSpeedThreshold" class="text-gray-700 px-2 py-0 text-sm h-6 rounded text-right flex-grow-0 w-16 ml-auto" />
                </div>
                <div class="option-child__info--wrapper">
                  <div class="option-child__info">Amount of m/sec you have to move to enable geolocation position capturing. Walking is around 1.2m – 1.4m / sec.<br>Default: 0.9</div>
                </div>
              </div>

              <div class="option-wrapper">
                <div class="option">
                  <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
                  <div class="mr-2 font-bold leading-none">Use Recording Time Interval</div>
                  <toggle-button
                    class="ml-auto"
                    color="#059605"
                    @change="onChangeUseRecordingTimeIntervalOnOff"
                    :sync="true"
                    :value="useRecordingTimeInterval"
                    :labels="true" />
                </div>
                <div class="option-child__info--wrapper">
                  <div class="option-child__info">Enable a time interval that has to elapse before a returned geolocation position is captured by the app.</div>
                </div>
              </div>

              <div class="option-wrapper" :class="{ 'hidden': !useRecordingTimeInterval }">
                <div class="option">
                  <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
                  <div class="mr-2 font-bold leading-none">Recording Time Interval</div>
                  <input type="text" v-model="recordingTimeInterval" class="h-6 w-16 ml-auto px-2 py-0 text-gray-700 text-sm text-right rounded flex-grow-0" />
                </div>
                <div class="option-child__info--wrapper">
                  <div class="option-child__info">Amount in m iliseconds that have to elapse before geolocation positions are capturing. The faster you move, the more you can crank it up.<br>Default: 1500</div>
                </div>
              </div>

            </div>

          </div>

          <!-- Notifications -->

          <p class="headline flex justify-between items-center" @click="toggleOptionsBlock($event, 'notifications')">
            Notifications
            <svg class="w-4 h-4 text-pink-200">
              <use xlink:href="#plus-circle" class="pointer-events-none" />
              <use xlink:href="#minus-circle" class="pointer-events-none invisible" />
            </svg>
          </p>

          <div class="options-block-wrapper" data-notifications>
            <div class="options-block">
              <div class="option-wrapper">
                <div class="option">
                  <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
                  <div class="mr-2 font-bold leading-none">Show notification when not moving</div>
                  <toggle-button
                    class="ml-auto"
                    color="#059605"
                    @change="onChangeShowNotificationWhenNotMovingOnOff"
                    :sync="true"
                    :value="showNotificationWhenNotMoving"
                    :labels="true" />
                </div>
                <div class="option-child__info--wrapper">
                  <div class="option-child__info">Requires minimum speed to be set.</div>
                </div>
              </div>
            </div>
          </div>

        </div>

        <hr class="divider divider-light" v-if="trackingAllowed">

        <!-- Allow sensors -->
        <div class="option-wrapper" :class="{ 'cursor-not-allowed opacity-50': recording }">
          <div class="option">
            <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
            <div class="mr-2 font-bold leading-none">Allow Sensors</div>
            <!--
              ":disabled" is a boolean, but "recording" could also be a string ("paused"),
              so check against "recording === 1"
            -->
            <toggle-button
              class="ml-auto"
              color="#059605"
              @change="onChangeSensorsAllowedOnOff"
              :sync="true"
              :value="sensorsAllowed"
              :disabled="recording === 1"
              :labels="true" />
          </div>

          <div class="option-child__info--wrapper">
            <div class="option-child__info">Allow the usage of various sensors (currently only the Gyroscope sensor) to enable the Pedometer (step counter).</div>
          </div>
        </div>

        <div class="settings-block w-full" :class="{ 'hidden': !sensorsAllowed }">

          <p class="headline flex justify-between items-center" @click="toggleOptionsBlock($event, 'sensors')">
            USE DEVICE SENSORS
            <svg class="w-4 h-4 text-pink-200">
              <use xlink:href="#plus-circle" class="pointer-events-none" />
              <use xlink:href="#minus-circle" class="pointer-events-none invisible" />
            </svg>
          </p>

          <div class="options-block-wrapper" data-sensors>

            <div class="options-block">

              <div class="option-wrapper" :class="{ 'cursor-not-allowed opacity-50': recording }">
                <div class="option">
                  <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
                  <div class="mr-2 font-bold leading-none">Use Pedometer</div>
                  <toggle-button
                    class="ml-auto"
                    color="#059605"
                    @change="onChangeUsePedometerOnOff"
                    :sync="true"
                    :value="usePedometer"
                    :labels="true" />
                </div>
                <div class="option-child__info--wrapper">
                  <div class="option-child__info">(De)Activate Gyroscope to count steps. Makes only sense with the proper activity chosen.</div>
                </div>
              </div>

            </div>

          </div>

        </div>

      <!-- </div> -->
    <!-- </div> -->

    <hr class="divider divider-light">

    <p class="headline">Zooming</p>

    <div class="option-wrapper">
      <div class="option">
        <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
        <div class="mr-2 font-bold leading-none">Zoom Level (near)</div>
        <input type="text" v-model="zoomLevelNear" class="w-16 h-6 ml-auto px-2 py-0 text-gray-700 text-sm text-right rounded flex-grow-0" />
      </div>
      <div class="option-child__info--wrapper">
        <div class="option-child__info">Prefered zoom level to which the app should zoom in when required. Standard: 16</div>
      </div>
    </div>

    <div class="option-wrapper">
      <div class="option">
        <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
        <div class="mr-2 font-bold leading-none">Zoom Level (far)</div>
        <input type="text" v-model="zoomLevelFar" class="w-16 h-6 ml-auto px-2 py-0 text-gray-700 text-sm text-right rounded flex-grow-0" />
      </div>
      <div class="option-child__info--wrapper">
        <div class="option-child__info">Prefered zoom level to which the app should zoom out when required. Standard: 0</div>
      </div>
    </div>

    <hr class="divider divider-light">

    <p class="headline">Device settings</p>

    <div class="option-wrapper">
      <div class="option">
        <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
        <div class="mr-2 font-bold leading-none">Enable Sceen Wake Lock</div>
        <toggle-button
          class="ml-auto"
          color="#059605"
          @change="onChangeScreenWakeLockAvailabilityOnOff"
          :sync="true"
          :value="screenWakeLockAvailability"
          :labels="true" />
      </div>
      <div class="option-child__info--wrapper">
        <div class="option-child__info">Prevents dimming or locking the screen. Increases battery usage. Recommended to be turned on while recording tracks.</div>
      </div>
    </div>

    <hr class="divider divider-light">

    <p class="headline">Speech Recognition<br><small class="text-pink-400">To be implemented yet</small></p>

    <div class="option-wrapper">
      <div class="option">
        <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
        <div class="mr-2 font-bold leading-none">Language</div>
        <select class="bg-white text-gray-700 rounded ml-auto px-2 py-0 text-sm h-6 w-16" v-model="speechRecognitionSelectedLanguage">
          <!-- <option v-for="(lang, index) in languages" :key="index" :value="lang.code">{{ lang.label }}</option> -->
          <option value="de-AT">de</option>
          <option value="en-US">en</option>
        </select>
      </div>
      <div class="option-child__info--wrapper">
        <div class="option-child__info">Choose the language you use to talk to your device.</div>
      </div>
    </div>

    <div class="option-wrapper">
      <div class="option">
        <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
        <div class="mr-2 font-bold leading-none">Command to Stop Recording</div>
        <input type="text" v-model="speechRecognitionStopCommand" class="w-16 h-6 ml-auto px-2 py-0 text-gray-700 text-sm text-right rounded flex-grow-0" />
      </div>
      <div class="option-child__info--wrapper">
        <div class="option-child__info">Language dependent<br>("stop" != "stopp").</div>
      </div>
    </div>

    <hr class="divider divider-light">

    <p class="headline">Pagination</p>

    <div class="option-wrapper">
      <div class="option">
        <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
        <div class="mr-2 font-bold leading-none">Map Hosts per Page</div>
        <input type="text" v-model="maxMapHostsPerPage" class="w-12 h-6 ml-auto px-2 py-0 text-gray-700 text-sm text-right rounded flex-grow-0" />
      </div>
      <div class="option-child__info--wrapper">
        <div class="option-child__info">The maximum number of map hosts shown on one page. Default: 10</div>
      </div>
    </div>

    <div class="option-wrapper">
      <div class="option">
        <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
        <div class="mr-2 font-bold leading-none">Notes per Page</div>
        <input type="text" v-model="maxNotesPerPage" class="w-12 h-6 ml-auto px-2 py-0 text-gray-700 text-sm text-right rounded flex-grow-0" />
      </div>
      <div class="option-child__info--wrapper">
        <div class="option-child__info">The maximum number of notes shown on one page. Default: 7</div>
      </div>
    </div>

    <div class="option-wrapper">
      <div class="option">
        <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
        <div class="mr-2 font-bold leading-none">Tracks per Page</div>
        <input type="text" v-model="maxTrackedRoutesPerPage" class="w-12 h-6 ml-auto px-2 py-0 text-gray-700 text-sm text-right rounded flex-grow-0" />
      </div>
      <div class="option-child__info--wrapper">
        <div class="option-child__info">The maximum number of tracks shown on one page. Default: 12</div>
      </div>
    </div>

    <hr class="divider divider-light">

    <p class="headline">Debugging & Logging</p>

    <div class="option-wrapper">
      <div class="option">
        <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
        <div class="mr-2 font-bold leading-none">Enable debugging</div>
        <toggle-button
          class="ml-auto"
          color="#059605"
          @change="onChangeDebuggingOnOff"
          :sync="true"
          :value="debugging"
          :labels="true" />
      </div>
        <div class="option-child__info--wrapper">
          <div class="option-child__info">Show/hide debugging buttons<br>Slows down app. Deactivate when not needed.</div>
        </div>
    </div>

    <div class="option-wrapper">
      <div class="option">
        <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
        <div class="mr-2 font-bold leading-none">Activate Console</div>
        <toggle-button
          class="ml-auto"
          color="#059605"
          @change="onChangeConsoleOnOff"
          :sync="true"
          :value="console"
          :labels="true" />
      </div>
      <div class="option-child__info--wrapper">
        <div class="option-child__info">(De)Activate console output<br>Deactivate when not needed.</div>
      </div>
    </div>

    <hr class="divider divider-light">

    <p class="headline">Updates</p>

    <div class="option-wrapper">
      <div class="option">
        <svg class="w-4 h-4 mr-2 flex-shrink-0 cursor-pointer" @click="toggleInfo"><use xlink:href="#info" class="pointer-events-none" /></svg>
        <div class="mr-2 font-bold leading-none">Check for<br>updates</div>
        <button class="btn ml-auto">To be integrated</button>
      </div>
      <div class="option-child__info--wrapper">
        <div class="option-child__info">More info on that soon.</div>
      </div>
    </div>

    <hr class="divider divider-light">

    <p class="headline">About the App</p>

    <div class="option-wrapper">
      <div class="option">
        <div class="mr-2 font-bold leading-none">Version</div>
        <span class="text-sm text-gray-200">v{{ appVersion }}</span>
      </div>
    </div>

    <div class="option-wrapper">
      <div class="option">
        <div class="mr-2 font-bold leading-none">Homepage</div>
        <span class="text-sm text-gray-200">
          <a :href="`https://${appHomepage}`" class="link-underline" target="_blank">{{ appHomepage }}</a>
        </span>
      </div>
    </div>

    <div class="option-wrapper">
      <div class="option">
        <div class="mr-2 font-bold leading-none">Author</div>
        <span class="text-sm text-gray-200">{{ appAuthorName }}</span>
      </div>
    </div>

    <div class="option-wrapper">
      <div class="option">
        <div class="mr-2 font-bold leading-none">Email</div>
        <span class="text-sm text-gray-200">
          <a :href="`mailto:${appAuthorEmail}`" class="link-underline">{{appAuthorEmail}}</a>
        </span>
      </div>
    </div>

    <div class="option-wrapper">
      <div class="option">
        <div class="mr-2 font-bold leading-none">License</div>
        <span class="text-sm text-gray-200">{{ appLicense }}</span>
      </div>
    </div>

  </div>
</template>

<script>
import { version, author, homepage, license } from '@/../package.json'
import gsap from 'gsap'
/**
 * When adding new settings option, make sure to add them to
 * VueX's initSettings method, too! Otherwise the initial
 * state will not be displayed correctly.
 */
export default {
  name: 'SettingsGeolocation',
  data () {
    return {
      //
      // Speech Recognition: move to computed once it is in GeoMover DB
      //
      speechRecognitionSelectedLanguage: 'en-US',
      speechRecognitionStopCommand: 'stop',
      appVersion: version,
      appAuthorName: author.name,
      appAuthorUrl: author.url,
      appAuthorEmail: author.email,
      appHomepage: homepage,
      appLicense: license
    }
  },
  computed: {
    dbReady: {
      get () { return this.$store.getters['settings/getDbReady'] }
    },
    //
    // Global feature permissions
    //
    trackingAllowed: {
      get () { return this.$store.getters['settings/getTrackingAllowed'] },
      set (val) { this.$store.dispatch('settings/setTrackingAllowed', val) }
    },
    sensorsAllowed: {
      get () { return this.$store.getters['settings/getSensorsAllowed'] },
      set (bool) { this.$store.dispatch('settings/setSensorsAllowed', bool) }
    },
    //
    // Geolocation
    //
    enableHighAccuracy: {
      get () { return this.$store.getters['settings/getEnableHighAccuracy'] },
      set (val) { this.$store.dispatch('settings/setEnableHighAccuracy', val) }
    },
    maximumAge: {
      get () { return this.$store.getters['settings/getMaximumAge'] },
      set (val) { this.$store.dispatch('settings/setMaximumAge', val) }
    },
    timeout: {
      get () { return this.$store.getters['settings/getTimeout'] },
      set (val) { this.$store.dispatch('settings/setTimeout', val) }
    },
    //
    // Geolocation Recoding contstraints
    //
    useMinSpeedThreshold: {
      get () { return this.$store.getters['settings/getUseMinSpeedThreshold'] },
      set (val) { this.$store.dispatch('settings/setUseMinSpeedThreshold', val) }
    },
    minSpeedThreshold: {
      get () { return this.$store.getters['settings/getMinSpeedThreshold'] },
      set (float) { this.$store.dispatch('settings/setMinSpeedThreshold', float) }
    },
    useRecordingTimeInterval: {
      get () { return this.$store.getters['settings/getUseRecordingTimeInterval'] },
      set (val) { this.$store.dispatch('settings/setUseRecordingTimeInterval', val) }
    },
    recordingTimeInterval: {
      get () { return this.$store.getters['settings/getRecordingTimeInterval'] },
      set (int) { this.$store.dispatch('settings/setRecordingTimeInterval', int) }
    },
    //
    // ----- Notification when not moving
    //
    showNotificationWhenNotMoving: {
      get () { return this.$store.getters['settings/getShowNotificationWhenNotMoving'] },
      set (bool) { this.$store.dispatch('settings/setShowNotificationWhenNotMoving') }
    },
    //
    // Recording: could be string ("paused") instead of booelan
    // therefore check the :disabled property against "recording !== 1"
    //
    recording: {
      get () {
        return this.$store.getters['newActivity/getRecording']
      }
    },
    //
    // Zoom
    //
    zoomLevelNear: {
      get () { return this.$store.getters['settings/getZoomLevelNear'] },
      set (val) { this.$store.dispatch('settings/setZoomLevelNear', val) }
    },
    zoomLevelFar: {
      get () { return this.$store.getters['settings/getZoomLevelFar'] },
      set (val) { this.$store.dispatch('settings/setZoomLevelFar', val) }
    },
    //
    // Address Services
    //
    addressService: {
      get () { return this.$store.getters['settings/getAddressService'] },
      set (val) { this.$store.dispatch('settings/setAddressService', val) }
    },
    //
    // Screen WakeLock
    //
    screenWakeLockAvailability: {
      get () { return this.$store.getters['settings/getScreenWakeLockAvailability'] },
      set (bool) { this.$store.dispatch('settings/setScreenWakeLockAvailability', bool) }
    },
    //
    // Pagination
    //
    maxMapHostsPerPage: {
      get () { return this.$store.getters['settings/getMaxMapHostsPerPage'] },
      set (int) { this.$store.dispatch('settings/setMaxMapHostsPerPage', int) }
    },
    maxNotesPerPage: {
      get () { return this.$store.getters['settings/getMaxNotesPerPage'] },
      set (int) { this.$store.dispatch('settings/setMaxNotesPerPage', int) }
    },
    maxTrackedRoutesPerPage: {
      get () { return this.$store.getters['settings/getMaxTrackedRoutesPerPage'] },
      set (int) { this.$store.dispatch('settings/setMaxTrackedRoutesPerPage', int) }
    },
    usePedometer: {
      get () { return this.$store.getters['settings/getUsePedometer'] },
      set (bool) { this.$store.dispatch('settings/setUsePedometer', bool) }
    },
    //
    // Debugging and logging
    //
    debugging: {
      get () { return this.$store.getters['settings/getDebugging'] },
      set (bool) { this.$store.dispatch('settings/setDebugging', bool) }
    },
    console: {
      get () { return this.$store.getters['settings/getConsole'] },
      set (bool) { this.$store.dispatch('settings/setConsole', bool) }
    },
    log: {
      get () { return this.$store.getters['settings/getLog'] },
      set (payload) { this.$store.dispatch('settings/setLog', payload) }
    }
  },
  mounted () {
    // this.$l.info('Settings','Geolocation: mounted invoked','')
  },
  methods: {
    /*
    toggleOuterGroup (e) {
      const svgIcon = e.target.lastElementChild
      const el = e.target.nextElementSibling
      const h = el.childNodes[0].getBoundingClientRect().height

      if (el.getBoundingClientRect().height === 0) {
        gsap.to(el, { duration: 0.3, height: h + 'px' })
        svgIcon.firstElementChild.classList.add('invisible')
        svgIcon.lastElementChild.classList.remove('invisible')
      }
      else {
        gsap.to(el, { duration: 0.3, height: 0 })
        svgIcon.firstElementChild.classList.remove('invisible')
        svgIcon.lastElementChild.classList.add('invisible')
      }
    },
    */
    toggleOptionsBlock: (e, whichOne) => {
      const svgIcon = e.target.firstElementChild
      const el = document.querySelector('[data-' + whichOne + ']')

      if (el.getBoundingClientRect().height === 0) {
        gsap.to(el, { duration: 0.3, height: 'auto' })
        svgIcon.firstElementChild.classList.add('invisible')
        svgIcon.lastElementChild.classList.remove('invisible')
      }
      else {
        gsap.to(el, { duration: 0.3, height: 0 })
        svgIcon.firstElementChild.classList.remove('invisible')
        svgIcon.lastElementChild.classList.add('invisible')
      }
    },
    toggleInfo: e => {
      const el = e.target.parentNode.nextSibling

      if (el.getBoundingClientRect().height === 0) {
        gsap.to(el, { duration: 0.3, height: 'auto' })
      }
      else {
        gsap.to(el, { duration: 0.3, height: 0 })
      }
    },
    //
    // Add "OnOff" to the name to make it different from the same-named event in
    // OpenLayers' geolocation object
    //
    // General permissions that the user has to give
    onChangeTrackingAllowedOnOff () {
      this.$store.dispatch('settings/setTrackingAllowed', !this.$store.getters['settings/getTrackingAllowed'])
    },
    onChangeSensorsAllowedOnOff () {
      this.$store.dispatch('settings/setSensorsAllowed', !this.$store.getters['settings/getSensorsAllowed'])
    },
    // Geolocation
    onChangeEnableHighAccuracyOnOff () {
      this.$store.dispatch('settings/setEnableHighAccuracy', !this.$store.getters['settings/getEnableHighAccuracy'])
    },
    onChangeScreenWakeLockAvailabilityOnOff () {
      this.$store.dispatch('settings/setScreenWakeLock', !this.$store.getters['settings/getScreenWakeLock'])
    },
    // Geolocation Recording constraints
    onChangeUseMinSpeedThresholdOnOff () {
      this.$store.dispatch('settings/setUseMinSpeedThreshold', !this.$store.getters['settings/getUseMinSpeedThreshold'])
    },
    onChangeMinSpeedThresholdOnOff () {
      this.$store.dispatch('settings/setMinSpeedThreshold', !this.$store.getters['settings/getMinSpeedThreshold'])
    },
    onChangeUseRecordingTimeIntervalOnOff () {
      this.$store.dispatch('settings/setUseRecordingTimeInterval', !this.$store.getters['settings/getUseRecordingTimeInterval'])
    },
    onChangeRecordingTimeIntervalOnOff () {
      this.$store.dispatch('settings/setRecordingTimeInterval', !this.$store.getters['settings/getRecordingTimeInterval'])
    },
    // Pedometer (gyroscope)
    onChangeUsePedometerOnOff () {
      this.$store.dispatch('settings/setUsePedometer', !this.$store.getters['settings/getUsePedometer'])
    },
    // Notification
    onChangeShowNotificationWhenNotMovingOnOff () {
      this.$store.dispatch('settings/setShowNotificationWhenNotMoving', !this.$store.getters['settings/getShowNotificationWhenNotMoving'])
    },
    // Debugging
    onChangeConsoleOnOff () {
      this.$store.dispatch('settings/setConsole', !this.$store.getters['settings/getConsole'])
    },
    onChangeDebuggingOnOff () {
      this.$store.dispatch('settings/setDebugging', !this.$store.getters['settings/getDebugging'])
    }
  }
  // watch: {
  //   recording: function (newVal, oldVal) {
  //     console.log('SettingsGeolocation: watch - invoked')
  //     console.log('> newVal =', newVal)
  //     console.log('> oldVal =', oldVal)
  //   }
  // }
}
</script>

<style lang="postcss">
.headline {
  @apply w-full;
  @apply flex justify-between items-center;
  @apply uppercase font-bold text-xs mb-3 text-pink-600;
  @apply select-none;
}

/*
.outer-group-wrapper {
  @apply w-full overflow-hidden;
}
.outer-group {
  @apply absolute w-full px-2 py-2 rounded-md bg-pink-700;
}
*/

/* Wraps many "option" units , therefore plural*/
.options-block-wrapper {
  @apply w-full h-0 mb-0 overflow-hidden;
}
.options-block {
  @apply mb-5 px-2 pt-2 rounded-md bg-pink-800 w-full
}
.settings-block {
  @apply pl-6 pt-4;
}

/* One option unit, therefore ungular */
.option-wrapper {
  @apply relative flex flex-col content-between w-full;
}
.option {
  @apply py-1;
  @apply flex flex-row flex-grow items-center justify-between select-none;
}

.option-child__info--wrapper {
  @apply w-full h-0 mb-2 overflow-hidden text-sm text-gray-200;
}
.option-child__info {
  @apply w-full pt-1 pb-2 pl-6;
}
</style>

<!--
    <div class="flex flex-col content-between w-full mb-12">
      <div class="flex flex-col flex-grow items-start justify-start mb-3">
        <div class="mr-2 font-bold leading-none">Address API</div>
        <span class="text-sm text-gray-200">Fetch local address of your current location via a Reverse Geocoding API.</span>
      </div>
      <div class="relative flex flex-col flex-grow items-start justify-start leading-loose">
        <label class="checkmark-label relative px-1 rounded-md w-full flex flex-row items-center cursor-pointer transition-colors duration-300 hover:bg-pink-700">
          <input type="radio" v- model="addressService" value="none" : checked="addressService === 'none'">
          <span class="checkmark"></span>
          <span class="ml-2">none</span>
        </label>
        <label class="checkmark-label relative px-1 rounded-md w-full flex flex-row items-center cursor-pointer transition-colors duration-300 hover:bg-pink-700">
          <input type="radio" v- model="addressService" value="nominatim" : checked="addressService === 'nominatim'">
          <span class="checkmark"></span>
          <span class="ml-2">nominatim</span>
        </label>
        <label class="checkmark-label relative px-1 rounded-md w-full flex flex-row items-center cursor-pointer transition-colors duration-300 hover:bg-pink-700">
          <input type="radio" v- model="addressService" value="here" : checked="addressService === 'here'">
          <span class="checkmark"></span>
          <span class="ml-2">HERE</span>
        </label>
      </div>
    </div>
-->
